import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Card } from '../common/Card';

interface CollapsibleStoryProps {
  story: string | undefined;
  isFacilitator: boolean;
  children?: React.ReactNode; // StoryInput component for facilitator
}

export function CollapsibleStory({ story, isFacilitator, children }: CollapsibleStoryProps) {
  const [isExpanded, setIsExpanded] = useState(!!story);

  // Update expanded state when story changes
  useEffect(() => {
    if (story) {
      setIsExpanded(true);
    }
  }, [story]);

  // Don't show if no story and not facilitator
  if (!story && !isFacilitator) {
    return null;
  }

  // If facilitator but no story, show collapsed input
  if (isFacilitator && !story && !isExpanded) {
    return (
      <Card>
        <button
          onClick={() => setIsExpanded(true)}
          className="w-full text-left flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors"
          aria-expanded="false"
        >
          <span className="text-sm font-medium text-gray-600">Story Description (Optional)</span>
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </Card>
    );
  }

  // Sanitize HTML content for safe rendering
  const sanitizedStory = story ? DOMPurify.sanitize(story, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'div', 'span'],
    ALLOWED_ATTR: ['style', 'class'],
  }) : '';

  return (
    <Card>
      <div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full text-left flex items-center justify-between mb-2"
          aria-expanded={isExpanded}
        >
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Story Description
          </h3>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isExpanded && (
          <div className="mt-2">
            {isFacilitator && children ? (
              children
            ) : story ? (
              <div 
                className="text-gray-800 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:mb-1 [&_strong]:font-bold [&_em]:italic [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-bold [&_h3]:mb-1 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_pre]:bg-gray-100 [&_pre]:p-2 [&_pre]:rounded [&_pre]:overflow-x-auto"
                dangerouslySetInnerHTML={{ __html: sanitizedStory }}
              />
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
}

